import { type FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import { updatePartnerMutationOptions, type UpdatePartnerPayload } from "../../api/partners";
import * as FormDialog from "../../shared/FormDialog";
import { useMutation } from "@tanstack/react-query";

type PartnerEditDialogProps = { onClose: () => void; partnerId: string; partnerName: string };

export const PartnerEditDialog: FC<PartnerEditDialogProps> = ({ onClose, partnerId, partnerName }) => {
  const { t } = useTranslation();

  const { handleSubmit, control, setFocus } = useForm<UpdatePartnerPayload>({
    values: { name: partnerName },
  });
  const updatePartnerMutation = useMutation(updatePartnerMutationOptions());

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleUpdate = (payload: UpdatePartnerPayload) => {
    updatePartnerMutation.mutate({ id: partnerId, payload }, { onSuccess: () => onClose() });
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("PartnersDrawer.Edit.DialogHeader")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleUpdate)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={updatePartnerMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("CreatePartner.Dialog.Error") }}
              label={t("CreatePartner.Dialog.NameLabel")}
              placeholder={t("CreatePartner.Dialog.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updatePartnerMutation.isPending}>
            {t("PartnersDrawer.Edit.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
